import { parse } from 'svg-parser';
import styleParse from 'style-to-object';

import type { Shapes, Coordinate } from '@clear-coat/mo_svg/src/types';

const DISTANCE_THRESHOLD = 0.02;

const FILL_REMOVE_REGEX = /\s+\.[aA-zZ]+[0-9]+/;

export type HighlightIndex = { shapeIndex: number; pointIndexes: number[] };

export interface ShapeInfo {
    unclosedCount: number;
    closeCount: number;
    highlightIndexes: HighlightIndex[];
}

const detectDistance = (coords: Coordinate[], distanceThreshold: number): number[] => {
    return coords.slice(1, -1).reduce((acc: number[], _, i) => {
        const distance = Math.sqrt((coords[i][0] - coords[i + 1][0]) ** 2 + (coords[i][1] - coords[i + 1][1]) ** 2);
        if (distance < distanceThreshold) {
            return [...acc, i + 1];
        }
        return acc;
    }, []);
};

export const getSVGInfo = (shapes: Shapes): ShapeInfo => {
    return shapes.reduce(
        (acc: ShapeInfo, shape, index) => {
            const firstCoords = shape[0];
            const lastCoords = shape[shape.length - 1];

            const closed = firstCoords.every((coord, i) => lastCoords[i] === coord);

            if (!closed) {
                acc.unclosedCount++;
            }

            const highlights = detectDistance(shape, DISTANCE_THRESHOLD);

            if (highlights.length) {
                acc.highlightIndexes = [
                    ...acc.highlightIndexes,
                    {
                        shapeIndex: index,
                        pointIndexes: highlights,
                    },
                ];
            }

            acc.closeCount += highlights.length;

            return acc;
        },
        { closeCount: 0, unclosedCount: 0, highlightIndexes: [] }
    );
};

export const checkIsHighlight = (highlights: HighlightIndex[], shapeIdx: number, pointIdx: number) => {
    return Boolean(
        highlights.find((highlight) => {
            const { shapeIndex, pointIndexes } = highlight;

            return shapeIndex === shapeIdx && pointIndexes.includes(pointIdx);
        })
    );
};

/**
 * Designed specifically for Adobe illustrator SVGs
 *
 * Searches for <style> tag, trims the class name and brackets, and parses the css
 * Then checks if fill is used
 *
 * In adobe, the SVG shapes will have a style class referenced from the
 * shape's properties
 *
 * That style class would defined within the style tag
 */
export const detectFill = (svg: string) => {
    const parsed = parse(svg);
    const svgChild: any = parsed.children.find((child: any) => child.tagName === 'svg');
    const [styleTag] = svgChild.children.filter((node: any) => node.tagName === 'style');
    if (!styleTag) {
        return false;
    }
    return styleTag.children.some((element: any) => {
        const trimmed = element.value.replace(FILL_REMOVE_REGEX, '').replace(/{/, '').replace(/}/, '');
        const elementJSON = styleParse(trimmed);
        if (!elementJSON || !elementJSON.fill) {
            return false;
        }
        return elementJSON.fill !== 'none';
    });
};

/**
 * Increment the version by one, or default to `'1'` if there is no previous version.
 */
export const bumpModelVersion = (version: string | undefined) => {
    return !version ? '1' : (parseInt(version) + 1).toString();
};
